import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import dayjs from 'dayjs';

// Components
import Tooltip from '@/components/base/Tooltip';
import Badge from '@/components/base/Badge';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

// lib
import userDateFormat from '@/lib/user/dateFormat';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

export type DeprecatedType = {
    status: boolean | string;
    type?: 'organic' | 'paid';
};

type DeprecatedKpiBadgeProps = {
    deprecated?: DeprecatedType;
    network?: SocialProfileNetworkType;
} & React.ComponentPropsWithoutRef<'span'>;

export function DeprecatedKpiBadge({
    deprecated,
    network,
    className,
    ...restProps
}: DeprecatedKpiBadgeProps) {
    const { t } = useTranslation('analytics');
    const { user } = useUser();

    const tooltipContent = () => {
        let token = 'deprecated_analytics';
        const values = {};
        if (network) {
            token += '_network';
            values['network'] = network.charAt(0).toUpperCase() + network.slice(1);
        }
        if (
            typeof deprecated?.status === 'string' &&
            dayjs(deprecated?.status, 'MM/DD/YYYY', true).isValid()
        ) {
            token += '_date';
            values['date'] = dayjs(deprecated?.status).format(
                userDateFormat[user?.settings?.date_format] ?? userDateFormat.month_day_year
            );
        }

        return t(token, values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    const badgeVariant = () => {
        switch (deprecated?.type) {
            case 'organic':
                return 'deprecated-organic';
            case 'paid':
                return 'deprecated-paid';
            default:
                return 'deprecated';
        }
    };

    if (!deprecated?.status) {
        return null;
    }
    return (
        <Tooltip content={tooltipContent()} placement="top">
            <span className={clsx('absolute', className)} {...restProps}>
                <Badge variant={badgeVariant()} />
            </span>
        </Tooltip>
    );
}
